import React from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'react-emotion';
import Color from 'color';
import shortid from 'shortid';
import toTitleCase from 'to-title-case';

import gray from '../utils/gray';
import { rte } from '../utils/styles';
import { breakpoints, colors, baselineGrid } from '../utils/presets';
import { options, rhythm } from '../utils/typography';
import { toRem, toVW } from '../utils/functions';

import Section from '../components/section';
import InnerWrapper from '../components/inner-wrapper';
import Button from '../components/button';

import { ReactComponent as DesktopAndGadgets } from '../assets/svgr/desktop-and-gadgets.svg';
import { ReactComponent as TumblrPostTypes } from '../assets/svgr/tumblr-post-types.svg';
import { ReactComponent as ThemeOptions } from '../assets/svgr/theme-options.svg';
import { ReactComponent as GoogleFonts } from '../assets/svgr/google-fonts.svg';
import { ReactComponent as SocialSharing } from '../assets/svgr/social-sharing.svg';
import { ReactComponent as CommentingSystems } from '../assets/svgr/commenting-systems.svg';

const featuresIcons = {
  DesktopAndGadgets,
  TumblrPostTypes,
  ThemeOptions,
  GoogleFonts,
  SocialSharing,
  CommentingSystems,
};

const themeFeatures = css`
  text-align: left;

  @media (max-width: ${breakpoints.lap - 1}px) {
    text-align: center;
  }
`;

const themeFeaturesWrapper = css`
  display: flex;
  flex-wrap: wrap;
`;

const themeFeaturesCell = css`
  position: relative;
  flex: 0 1 100%;
  padding: ${toRem(baselineGrid * 4)} calc(${toRem(baselineGrid * 2)} + ${toVW(baselineGrid * 4)});

  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    z-index: -1;
    pointer-events: none;
    border-color: ${gray(80, 'cool')};
    border-style: solid;
    border-width: 0;
  }

  &::before {
    top: 0;
    bottom: 0;
    left: 50%;
    width: ${toRem(baselineGrid * 12)};
    transform: translate(-50%, 0);
    border-top-width: 1px;
  }

  &::after {
    left: 0;
    right: 0;
    top: 50%;
    height: ${toRem(baselineGrid * 12)};
    transform: translate(0, -50%);
    border-right-width: 1px;
  }

  @media (max-width: ${breakpoints.lap - 1}px) {
    /* outline: 1px dotted red; */
    &::after {
      border-right-width: 0;
    }

    &:first-of-type {
      &::before {
        border-top-width: 0;
      }
    }
  }

  @media (min-width: ${breakpoints.lap}px) and (max-width: ${breakpoints.lapLarge - 1}px) {
    flex: 0 1 50%;

    &:nth-child(-n + 2) {
      &::before {
        border-top-width: 0;
      }
    }

    &:nth-child(2n + 2) {
      &::after {
        border-right-width: 0;
      }
    }
  }

  @media (min-width: ${breakpoints.lapLarge}px) {
    flex: 0 1 33.3333%;

    &:nth-child(-n + 3) {
      &::before {
        border-top-width: 0;
      }
    }

    &:nth-child(3n + 3) {
      &::after {
        border-right-width: 0;
      }
    }
  }
`;

const themeFeaturesIcon = css`
  display: block;
  max-width: ${toRem(baselineGrid * 28)};
  fill: ${colors.gray.gunpowder};
  isolation: isolate;

  @media (max-width: ${breakpoints.lap - 1}px) {
    margin: 0 auto;
  }

  @media (min-width: ${breakpoints.lap}px) and (max-width: ${breakpoints.lapLarge - 1}px) {
    margin: 0 0 ${rhythm(options.blockMarginBottom)} 0;
  }

  @media (min-width: ${breakpoints.lapLarge}px) {
    margin: ${rhythm(options.blockMarginBottom)} 0;
  }

  [class^='fill'] {
    mix-blend-mode: screen;
  }

  .fill1 {
    fill: ${colors.pixelmoxie};
    fill-opacity: 0.4;
  }

  .fill2 {
    fill: ${colors.accent.green};
    fill-opacity: 0.5;
  }
`;

const altColors = css`
  .fill1 {
    fill: ${colors.purple};
    fill-opacity: 0.35;
  }

  .fill2 {
    fill: ${colors.accent.yellow};
    fill-opacity: 0.6;
  }
`;

const themeFeaturesTitle = css`
  margin-bottom: ${rhythm(options.blockMarginBottom * 0.5)};
`;

const themeFeaturesPlug = css``;

const ctaWrap = css`
  text-align: center;
`;

const cta = css`
  margin-left: auto;
  margin-right: auto;
`;

const featuresData = [
  {
    icon: 'DesktopAndGadgets',
    title: 'One Design For All Screens',
    description:
      'Fully responsive and retina ready, mobile-first design. Looks great on all devices.',
  },
  {
    icon: 'ThemeOptions',
    title: 'Plenty of Theme Options',
    description: 'Change fonts, colors, layouts, show or hide elements, and more.',
  },
  {
    icon: 'GoogleFonts',
    title: 'Google Fonts Ready',
    description: 'Customize headings and more with families from the Google Web Fonts repository.',
  },
  {
    icon: 'TumblrPostTypes',
    title: 'Support For All Post Types',
    description: 'Text, Photo, Quote, Link, Chat, Audio, Video and Answer posts are all supported.',
  },
  {
    icon: 'SocialSharing',
    title: 'Social Sharing',
    description: 'Share your posts on the main social media outlets with minimal fuss.',
  },
  {
    icon: 'CommentingSystems',
    title: 'Comments Support',
    description: 'Out of the box support for Disqus and Facebook commenting systems.',
  },
];

const ThemeFeaturesCell = ({ data }) => {
  const { icon, title, description, className, alt } = data;
  const Icon = featuresIcons[icon];

  return (
    <div className={cx(themeFeaturesCell, className)}>
      <Icon className={cx(themeFeaturesIcon, { [altColors]: alt })} />
      <h4 className={themeFeaturesTitle}>{toTitleCase(title)}</h4>
      <p className={themeFeaturesPlug}>{description}</p>
    </div>
  );
};

ThemeFeaturesCell.propTypes = {
  data: PropTypes.object.isRequired,
};

const ThemeFeatures = ({ name, features }) => {
  let featuresArray = featuresData;

  // Let individual themes override features entries.
  if (features) {
    const map = new Map(features.map(feat => [feat.icon, feat]));
    featuresArray = featuresData.map(feature => {
      const { icon } = feature;
      const override = map.get(icon) || {};

      return {
        icon: String(icon),
        title: override.title || feature.title,
        description: override.description || feature.description,
      };
    });
  }

  return (
    <div className={cx(themeFeatures, rte)}>
      <InnerWrapper narrow>
        <Section title={toTitleCase(`Also included with ${name}`)}>
          <div className={themeFeaturesWrapper}>
            {featuresArray.map((data, index) => {
              data.alt = Boolean(index % 2);
              return <ThemeFeaturesCell key={shortid.generate()} data={data} />;
            })}
          </div>
        </Section>
        <Section>
          <div className={ctaWrap}>
            <Button
              to={'/tumblr-themes/'}
              className={cta}
              backgroundColor={Color(colors.blue)
                .hsl()
                .lighten(0.1)
                .desaturate(0.1)
                .string()}
              large={true}
              arrow={true}
            >
              {'Browse More Themes'}
            </Button>
          </div>
        </Section>
      </InnerWrapper>
    </div>
  );
};

ThemeFeatures.propTypes = {
  name: PropTypes.string.isRequired,
  features: PropTypes.array,
};

export default ThemeFeatures;

export const themeFeaturesFragment = graphql`
  fragment themeFeatures_details on MarkdownRemark {
    frontmatter {
      name
      features {
        icon
        title
        description
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/components/themes-features.js